/*
Copyright (c) 2025, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { RefreshWrapperProps } from './types';
import { useLottie } from 'lottie-react';
import * as loader from './animationData/loader.json';
import { generateID } from '../../base/helper';
import './styles.scss';
import { withAnalytics } from '@veneer/analytics';

const MAX_PULL_DISTANCE = 60;
const REFRESH_TRIGGER_DISTANCE = 20;

const TRefreshWrapper: React.FC<RefreshWrapperProps> = ({
  id = generateID('clientos-ui-toolkit-refresh'),
  onRefresh,
  isRefreshing = false,
  background,
  children,
  pullToRefreshText = 'Pull down to refresh',
  releaseToRefreshText = 'Release to refresh',
  refreshingText = 'Refreshing',
  refreshedText = 'Refreshed'
}) => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [liveMessage, setLiveMessage] = useState('Pull down to refresh');
  const [livePoliteness, setLivePoliteness] = useState<'polite' | 'assertive'>(
    'polite'
  );
  const startY = useRef<number | null>(null);

  const { View } = useLottie({
    animationData: loader
  });

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (pulling) return;
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
        setPulling(true);
      }
      setLiveMessage('');
      setTimeout(() => {
        setLiveMessage(pullToRefreshText);
      }, 100);
    },
    [pulling, pullToRefreshText]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!pulling || startY.current === null) return;
      const distance = e.touches[0].clientY - startY.current;
      if (distance > 0) {
        // Non-linear (ease-out) pull effect
        const easedDistance =
          MAX_PULL_DISTANCE * (1 - Math.exp(-distance / MAX_PULL_DISTANCE));
        setPullDistance(Math.min(easedDistance, MAX_PULL_DISTANCE));

        setLiveMessage('');
        setTimeout(() => {
          setLiveMessage(releaseToRefreshText);
        }, 100);
      }
    },
    [pulling, MAX_PULL_DISTANCE, releaseToRefreshText]
  );

  const handleTouchCancel = useCallback(() => {
    setPulling(false);
    setPullDistance(0);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance > REFRESH_TRIGGER_DISTANCE) {
      onRefresh && onRefresh();
    }
    setPulling(false);
    setPullDistance(0);

    setLiveMessage('');
    setTimeout(() => {
      setLiveMessage(refreshingText);
    }, 100);
    setLivePoliteness('assertive');
  }, [pullDistance, REFRESH_TRIGGER_DISTANCE, onRefresh, refreshingText]);

  // Reset liveMessage to 'Pull down to refresh' when refresh is done
  useEffect(() => {
    if (!isRefreshing) {
      setLiveMessage('');
      setTimeout(() => {
        setLiveMessage('Pull down to refresh');
        setLivePoliteness('polite');
      }, 100);
    }
  }, [isRefreshing]);

  // Announce 'refreshed' when refresh is complete, then reset to 'Pull down to refresh'
  useEffect(() => {
    if (!isRefreshing) {
      setLiveMessage('');
      setTimeout(() => {
        setLiveMessage(refreshedText);
        setLivePoliteness('assertive');
        setTimeout(() => {
          setLiveMessage(pullToRefreshText);
          setLivePoliteness('polite');
        }, 1000); // Wait 1s before resetting
      }, 100);
    }
  }, [isRefreshing, refreshedText, pullToRefreshText]);

  return (
    <div
      id={id}
      className="ui-toolkit-refresh"
      style={{ background: background || 'inherit' }}
    >
      <div
        aria-live={livePoliteness}
        role={livePoliteness === 'assertive' ? 'alert' : undefined}
        style={{
          position: 'absolute',
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          border: 0,
          top: 0,
          left: 0
        }}
      >
        {liveMessage}
      </div>
      {(pulling && pullDistance > 20 && !isRefreshing) || isRefreshing ? (
        <div className="ui-toolkit-refresh-bar" />
      ) : null}
      <div
        className={`ui-toolkit-refresh-animation${isRefreshing ? ' refreshing-animation' : ''}`}
      >
        {View}
      </div>
      <div
        onTouchStart={pulling || isRefreshing ? undefined : handleTouchStart}
        onTouchMove={pulling && !isRefreshing ? handleTouchMove : undefined}
        onTouchEnd={pulling && !isRefreshing ? handleTouchEnd : undefined}
        onTouchCancel={pulling && !isRefreshing ? handleTouchCancel : undefined}
        className={`ui-toolkit-refresh-content${isRefreshing ? ' refreshing' : ''}`}
        style={{
          transform: pulling ? `translateY(${pullDistance}px)` : 'none'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const RefreshWrapperAnalytics = withAnalytics(TRefreshWrapper);
export default TRefreshWrapper;
